<template>
  <v-container class="max-width">
    <v-card>
      <v-toolbar color="secondary" flat dense>
        <v-toolbar-title>Stock Take</v-toolbar-title>
        <v-spacer></v-spacer>
      </v-toolbar>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="6" v-if="!hasStockTakeReport">
            <v-btn color="primary" class="mt-2" small to="/new-stock-take">
              <v-icon left dark>mdi-plus</v-icon> Record Stock Take
            </v-btn>
            <v-btn color="success" class="mt-2 ml-2" small @click="openStockTakeReport" :loading="reportLoader">
              <v-icon left dark>mdi-clipboard-text</v-icon> View Stock Take Report
            </v-btn>
          </v-col>

          <v-col cols="12" md="6" v-if="!hasStockTakeReport">
            <v-btn color="red" text class="mt-2 float-right" @click="closeStock" :loading="loader">
              <v-icon left dark>mdi-plus</v-icon> Close Stock Take
            </v-btn>
          </v-col>



        </v-row>

        <v-row>
          <v-col cols="12">
            <!-- Search field -->
            <v-text-field v-if="hasStockTakeReport" v-model="reportSearch" append-icon="mdi-magnify" label="Search"
              single-line hide-details class="mb-4"></v-text-field>
            <v-text-field v-else v-model="search" append-icon="mdi-magnify" label="Search" single-line hide-details
              class="mb-4"></v-text-field>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <!-- excel icon -->
            <download-excel :data="stockTakeReports" :fields="fields" name="Stock Take Data" class="mb-2">
              Download <v-icon color="green">mdi-file-excel</v-icon>
            </download-excel>
            <!-- end -->
          </v-col>
          
          <v-col cols="12" sm="12" md="12" class="subtitle-2 text--primary">
            <!-- Stock Take Report Table -->
            <v-data-table v-if="hasStockTakeReport" :headers="reportHeaders" :items="stockTakeReports"
              :search="reportSearch" :loading="reportLoader" loading-text="Loading stock take reports..."
              items-per-page="25" class="font-weight-medium">
              <template v-slot:item.PreviousQty="{ item }">
                <span>{{ item.PreviousQty | number }}</span>
              </template>

              <template v-slot:item.StockTaken="{ item }">
                <span>{{ item.StockTaken | number }}</span>
              </template>

              <template v-slot:item.Variance="{ item }">
                <span :class="item.Variance >= 0 ? 'black--text' : 'red--text'">
                  {{ item.Variance | number }}
                </span>
              </template>

              <template v-slot:item.date="{ item }">
                <span>{{ item.date | date }}</span>
              </template>

              <template v-slot:no-data>
                <v-alert :value="true" color="info" icon="mdi-information">
                  No stock take reports found for this store.
                </v-alert>
              </template>
            </v-data-table>

            <!-- Stock Take Records Table -->
            <v-data-table v-else :mobile-breakpoint="0" :headers="headers" :items="purchases" :search="search"
              items-per-page="2500">
              <template v-slot:item.DocTotal="{ item }">
                <span>{{ item.DocTotal | currency }}</span>
              </template>

              <template v-slot:item.Createdat="{ item }">
                <span>{{ item.CreatedAt | date }}</span>
              </template>

              <template v-slot:item.action="{ item }">
                <v-btn text color="accent" :to="`/stock-take/${item.id}`">
                  <v-icon left>mdi-eye</v-icon> More Details
                </v-btn>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- stock take dialog -->
    <v-dialog v-model="stockTakeDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card>
        <v-toolbar flat color="secondary" dense>
          <v-toolbar-title>
            <v-btn icon @click.native="stockTakeDialog = false">
              <v-icon dark>mdi-keyboard-backspace</v-icon>
            </v-btn>
            Stock Take Dialog
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click.native="stockTakeDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text>
          <v-container class="docForm">
            <v-row>
              <v-text-field v-model="search" append-icon="mdi-magnify" label="Search" single-line
                hide-details></v-text-field>
            </v-row>

            <v-row justify="center" class="subtitle-2 text--primary">
              <v-col cols="12">
                <v-data-table :search="search" :loading="loader" loading-text="Loading Data......"
                  :headers="stockHeaders" :items="inventory" :items-per-page="2000">
                  <template v-slot:item.AvailableQty="{ item }">
                    <span>{{ item.purchaseUomQty }}</span>
                  </template>

                  <template v-slot:item.PurchaseUnit="{ item }">
                    <span v-if="item.HasUom == 1 && item.BaseStockActive == 'N'">{{ item.PurchaseUnit }}</span>
                  </template>

                  <template v-slot:item.BaseUnitStock="{ item }">
                    <span v-if="item.HasUom == 1 && item.BaseStockActive == 'N'">{{ item.BaseUnitStock }}</span>
                  </template>

                  <template v-slot:item.BaseUnit="{ item }">
                    <span v-if="item.HasUom == 1 && item.BaseStockActive == 'N'">{{ item.BaseUnit }}</span>
                  </template>

                </v-data-table>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>
    <!-- end -->

    <!-- Stock Take Report Dialog -->
    <v-dialog v-model="reportDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card>
        <v-toolbar color="success" dark dense>
          <v-toolbar-title>
            <v-btn icon @click="reportDialog = false">
              <v-icon dark>mdi-keyboard-backspace</v-icon>
            </v-btn>
            Stock Take Report
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="reportDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-4">
          <v-container>
            <v-row justify="center">
              <v-col cols="10">
                <v-text-field v-model="reportSearch" append-icon="mdi-magnify" label="Search reports..." single-line
                  hide-details class="mb-4"></v-text-field>

                <v-data-table :headers="reportHeaders" :items="stockTakeReports" :search="reportSearch"
                  :loading="reportLoader" loading-text="Loading stock take reports..." items-per-page="25"
                  class="elevation-1 font-weight-medium">
                  <template v-slot:item.PreviousQty="{ item }">
                    <span>{{ item.PreviousQty | number }}</span>
                  </template>

                  <template v-slot:item.StockTaken="{ item }">
                    <span>{{ item.StockTaken | number }}</span>
                  </template>

                  <template v-slot:item.Variance="{ item }">
                    <span :class="item.Variance >= 0 ? 'green--text' : 'red--text'">
                      {{ item.Variance | number }}
                    </span>
                  </template>

                  <template v-slot:item.date="{ item }">
                    <span>{{ item.date | date }}</span>
                  </template>

                  <template v-slot:no-data>
                    <v-alert :value="true" color="warning" icon="mdi-alert">
                      No stock take reports found for this store.
                    </v-alert>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="reportDialog = false">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- End Stock Take Report Dialog -->

    <snackbar ref="snackbar"></snackbar>

  </v-container>
</template>

<script>
export default {
  computed: {
    activeStore() {
      return localStorage.getItem("store_id");
    },
    hasStockTakeReport() {
      return this.stockTakeReports && this.stockTakeReports.length > 0;
    },
  },
  data: () => ({
    stockTakeDialog: false,
    reportDialog: false,
    loader: false,
    reportLoader: false,
    search: "",
    reportSearch: "",
    TotalPurchases: null,
    TotalPayments: null,
    TotalBalance: null,
    inventory: [],
    stockTake: [],
    stockTakeReports: [],
    headers: [
      { text: "Total", value: "DocTotal" },
      { text: "Date", value: "CreatedAt" },
      { text: "Action", value: "action" },
    ],
    stockHeaders: [
      { text: "Item Code", value: "ItemCode" },
      { text: "Product Name", value: "Name" },
      { text: "Previous Qty", value: "AvailableQty" },
      { text: "Stock taken", value: "totalStockTaken" },
      { text: "Variance", value: "stockVariance" },
    ],
    reportHeaders: [
      { text: "Product Name", value: "product.Name" },
      { text: "Previous Qty", value: "PreviousQty" },
      { text: "Stock Taken", value: "StockTaken" },
      { text: "Variance", value: "Variance" },
      // { text: "Date", value: "date" },
    ],
    fields: {
      ProductName: "product.Name",
      PreviousQty: "PreviousQty",
      StockTaken: "StockTaken",
      Variance: "Variance",

    },
    purchases: [],
  }),
  methods: {
    updateStockTake() {
      const self = this;
      self.loader = true;
      this.$store
        .dispatch("get", `/update-stock-take`)
        .then((res) => {
          if (res.ResultCode == 1200) {
            self.loader = false;
            location.reload();
            self.$refs.snackbar.show("stock take closed successfully", "green");
          } else {
            this.$refs.snackbar.show(res.msg, "red");
            self.loader = false;
          }
        })
        .catch((err) => {
          this.$refs.snackbar.show(err, "red");
          self.loader = false;
        });


      //  this.$refs.snackbar.show("Stock take closed successfully", "green");
    },
    closeStock() {
      const id = this.activeStore;
      const self = this;
      self.loader = true;
      this.$store
        .dispatch("get", `/analyze-stock-take/${id}`)
        .then((res) => {
          if (res.ResultCode == 1200) {
            self.loader = false;
            self.inventory = res.ResponseData;
            self.$refs.snackbar.show("stock take closed successfully", "green");
            // Fetch stock take report after closing stock
            self.getStockTakeReport();
          } else {
            this.$refs.snackbar.show(res.msg, "red");
            self.loader = false;
          }
        })
        .catch((err) => {
          this.$refs.snackbar.show(err, "red");
          self.loader = false;
        });


      //  this.$refs.snackbar.show("Stock take closed successfully", "green");
    },
    getStockTakeReport() {
      const id = this.activeStore;
      const url = `/stock-take-report/${id}`;
      const self = this;
      self.reportLoader = true;
      this.$store
        .dispatch("get", url)
        .then((res) => {
          console.log(res, "stock take reports");
          if (res.ResultCode == 1200) {
            self.stockTakeReports = res.ResponseData || [];
            self.reportLoader = false;
            // Data loaded successfully - will be displayed on page
          } else {
            self.reportLoader = false;
            self.stockTakeReports = [];
            if (res.msg && !res.msg.includes('no data') && !res.msg.includes('not found')) {
              this.$refs.snackbar.show(res.msg, "red");
            }
          }
        })
        .catch((err) => {
          self.reportLoader = false;
          self.stockTakeReports = [];
          console.log('Stock take report fetch error:', err);
        });
    },
    openStockTakeReport() {
      this.reportDialog = true;
      this.getStockTakeReport();
    },
    getRecords() {
      const self = this;
      self.loader = true;
      this.$store
        .dispatch("get", `/stock-take-records`)
        .then((res) => {
          if (res.ResultCode == 1200) {
            self.purchases = res.ResponseData || [];
            self.loader = false;
          } else {
            self.loader = false;
            this.$refs.snackbar.show(res.msg, "red");
          }
        })
        .catch((err) => {
          self.loader = false;
          this.$refs.snackbar.show(err, "red");
        });
    },
  },
  created() {
    this.getRecords();
    // Auto-fetch stock take report on page load
    this.getStockTakeReport();
  },
};
</script>